/**
 * Single-session enforcement, client side.
 *
 * One live session per platform per user. This module owns three things:
 * who this device says it is (headers on every request), the 60-second poll
 * that discovers an eviction the WS frame missed, and the decision to act on
 * a `force_logout` frame.
 *
 * The poll — not the frame — is the guarantee. The frame is best-effort and
 * is missed whenever Redis is down, the socket had dropped, or the app was
 * closed at the moment of eviction.
 */
import { sessionGet, sessionRemove, clearSessionBackup } from "./sessionStore";

// Trailing slash stripped: "https://api.teamlio.ai/" + "/api/..." is "//api/...",
// which FastAPI 404s — the prod .env had exactly that.
const API_BASE_URL = (process.env.REACT_APP_API_BASE_URL || "http://localhost:8000").replace(/\/$/, "");

const DEVICE_ID_KEY = "teamlio.session.deviceId.v1";
const EVICTION_KEY = "teamlio.session.evictedBy";
const HEARTBEAT_MS = 60_000;

export const SESSION_PLATFORM = "teamlio_web" as const;

let cachedDeviceId: string | null = null;
// Held only when storage is blocked (see the catch below) — a random id for
// the life of this page, never written anywhere.
let noStorageDeviceId: string | null = null;

function webDeviceId(): string {
  try {
    const existing = localStorage.getItem(DEVICE_ID_KEY);
    if (existing) return existing;
    const generated =
      globalThis.crypto?.randomUUID?.() ??
      `web-${Date.now()}-${Math.random().toString(16).slice(2)}`;
    localStorage.setItem(DEVICE_ID_KEY, generated);
    return generated;
  } catch {
    // Private mode with storage blocked. A per-tab id is wrong (it would
    // evict on every reload), but a fixed string across users would be much
    // worse: every storage-blocked browser on every machine would present
    // the same id, so the backend would treat all of them as one device and
    // never evict between two different physical machines — silently
    // defeating single-session enforcement for exactly these users.
    //
    // Generate a random id once and hold it in memory for this page's
    // lifetime instead. Trade-off: a reload gets a fresh id and evicts the
    // previous one. That's the correct failure mode — worse than a normal
    // persisted device id, but strictly better than two machines silently
    // sharing a slot — so don't "fix" it by reaching for a constant again.
    if (!noStorageDeviceId) {
      noStorageDeviceId =
        globalThis.crypto?.randomUUID?.() ??
        `web-nostorage-${Date.now()}-${Math.random().toString(16).slice(2)}`;
    }
    return noStorageDeviceId;
  }
}

/**
 * This browser's stable device id.
 *
 * MUST live in localStorage, not sessionStorage. This app deliberately keeps
 * the ACCESS TOKEN per-tab (see src/api/api.ts) so two tabs can hold two
 * accounts — but the DEVICE is still one device. A per-tab id would make
 * every newly opened tab look like a new laptop and evict the previous one.
 */
export async function getSessionDeviceId(): Promise<string> {
  if (cachedDeviceId) return cachedDeviceId;
  cachedDeviceId = webDeviceId();
  return cachedDeviceId;
}

/** A human-readable name for "you were signed out by ..." on the other device. */
export function getSessionDeviceName(): string {
  const ua = navigator.userAgent;
  const browser = /Firefox\//.test(ua)
    ? "Firefox"
    : /Edg\//.test(ua)
      ? "Edge"
      : /Chrome\//.test(ua)
        ? "Chrome"
        : /Safari\//.test(ua)
          ? "Safari"
          : "Browser";
  return `${browser} on ${navigator.platform || "web"}`;
}

/**
 * Headers that let the backend tell this client apart from every other one.
 *
 * No X-Client-Version: REACT_APP_VERSION is set by neither app's build, so
 * the header was always "" and the backend's client_version column was
 * permanently blank. Dropped rather than wired up, to avoid touching the
 * release build scripts of two apps for a column nothing reads yet — if a
 * real need for it shows up, wire REACT_APP_VERSION from package.json in
 * both repos' build scripts and re-add this line to both session.ts copies.
 */
export async function sessionHeaders(): Promise<Record<string, string>> {
  return {
    "X-Client-Platform": SESSION_PLATFORM,
    "X-Client-Device-Id": await getSessionDeviceId(),
    "X-Client-Device-Name": getSessionDeviceName(),
    "X-Client-Os": navigator.platform || "",
  };
}

/**
 * Should this client sign itself out because of `frame`?
 *
 * Pure, and the only place the rule lives:
 *  - the frame must be for MY platform (taking the web slot must not kill the
 *    desktop app), and
 *  - it must not name MY device (that is my own fresh login racing my old
 *    socket, not an eviction).
 */
export function shouldForceLogout(
  frame: unknown,
  me: { platform: string; deviceId: string },
): boolean {
  if (!frame || typeof frame !== "object") return false;
  const f = frame as Record<string, unknown>;
  if (f.type !== "force_logout") return false;
  if (typeof f.platform !== "string" || f.platform !== me.platform) return false;
  // A malformed frame with no device_id must never evict — without this,
  // `undefined !== me.deviceId` would read as "some other device" and sign
  // the user out on the strength of a broken frame.
  if (typeof f.device_id !== "string" || f.device_id.length === 0) return false;
  return f.device_id !== me.deviceId;
}

/**
 * Is this 401 `detail` the backend saying the ACCOUNT itself is closed?
 *
 * Two ways that happens, and neither can be refreshed out of:
 * deactivated ("Your account has been deactivated. ...",
 * ACCOUNT_DEACTIVATED_DETAIL) and sign-in locked by an admin ("This account
 * has been locked by an administrator. ...", ADMIN_LOCKED_DETAIL in
 * hrms/utils/login_lockout.py). `get_current_user` and `/api/auth/refresh`
 * both answer with one of them from the moment the admin acts.
 *
 * An ordinary expired-token 401 must NOT be treated the same way — a refresh
 * may still save that session — so the match is on the body, not the status.
 * The three-wrong-passwords lock is deliberately not here: it never reaches a
 * signed-in client, only the sign-in form, and as a 423.
 */
export function isAccountClosedDetail(detail: unknown): detail is string {
  return (
    typeof detail === "string" &&
    /deactivated|locked by an administrator/i.test(detail)
  );
}

/**
 * The complete sentence to show for a `force_logout` frame.
 *
 * A frame that closes the ACCOUNT — deactivated, deleted, or its sign-in
 * locked by an admin — has no other device to name; telling the user their
 * account "was used on another device" would be a lie that sends them
 * chasing a takeover. Every other frame is an eviction and names the device
 * that won.
 */
export function forceLogoutMessage(frame: {
  reason?: unknown;
  device_name?: unknown;
}): string | null {
  if (frame.reason === "account_deactivated" || frame.reason === "account_deleted") {
    return "Your account has been deactivated. Contact your administrator.";
  }
  if (frame.reason === "account_locked") {
    // Says what will and will not help, because the obvious next move —
    // 'Forgot password' — is shut too for this one.
    return "Your sign-in has been locked by an administrator. 'Forgot password' will not reopen it — contact your admin.";
  }
  return typeof frame.device_name === "string"
    ? `You were signed out because your account was used on ${frame.device_name}.`
    : null;
}

/**
 * Sign out, loudly.
 *
 * `message` MUST already be a complete, ready-to-render sentence — the login
 * screen displays it verbatim with no wrapping. This is the second time this
 * exact bug has shipped (a bare device name stored here, then wrapped again
 * by the login screen, produced doubled-up garbage like "...used on Signed
 * in on another device (Chrome on MacIntel)."): the WS eviction frame really
 * does carry a bare device_name, so ITS call site is the one that must build
 * the sentence before calling forceLogout; the heartbeat's 409 `detail` is
 * already a full sentence from the backend and must be passed straight
 * through, untouched. Never reintroduce a "device name" parameter here.
 *
 * The message is stashed in sessionStorage so the login screen can explain
 * itself. A silent forced logout is indistinguishable from a bug — which is
 * exactly what made the v1.10.0 bundle rename painful (messenger's incident,
 * not this app's, but the lesson holds here too).
 */
export function forceLogout(message?: string | null): void {
  try {
    sessionStorage.setItem(
      EVICTION_KEY,
      message || "You were signed out because your account was used on another device.",
    );
  } catch {
    /* nothing to explain with — still sign out */
  }
  try {
    // Capture the evicted session's own token BEFORE removing it, and pass it
    // as the guard to clearSessionBackup. This app keeps the access token
    // per-tab so two tabs can hold two different accounts (see
    // sessionStore.ts) — an unconditional clear here would destroy a
    // DIFFERENT account's backup if it happened to be the most recently
    // persisted one, exactly the failure AuthContext.logout's own call
    // already guards against.
    const token = sessionGet("accessToken");
    sessionRemove("accessToken");
    sessionRemove("refreshToken");
    clearSessionBackup(token);
  } catch {
    /* ignore storage errors */
  }
  if (typeof window !== "undefined" && window.location.pathname !== "/login") {
    window.location.href = "/login";
  }
}

/** The complete eviction message for the login screen to render verbatim. Reads once. */
export function takeEvictionMessage(): string | null {
  try {
    const value = sessionStorage.getItem(EVICTION_KEY);
    if (value) sessionStorage.removeItem(EVICTION_KEY);
    return value;
  } catch {
    return null;
  }
}

/**
 * Poll every 60s; a 409 means another device owns this slot now.
 *
 * Returns a cleanup function. Errors other than 409 are ignored on purpose:
 * a flaky network must not sign anyone out.
 */
export function startSessionHeartbeat(): () => void {
  let stopped = false;

  const beat = async () => {
    if (stopped) return;
    let token: string | null = null;
    try {
      token = sessionGet("accessToken");
    } catch {
      return;
    }
    if (!token) return;

    try {
      const res = await fetch(`${API_BASE_URL}/api/auth/session/heartbeat`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          platform: SESSION_PLATFORM,
          device_id: await getSessionDeviceId(),
        }),
      });
      if (res.status === 409 && !stopped) {
        const body = await res.json().catch(() => ({}));
        forceLogout(typeof body?.detail === "string" ? body.detail : null);
      } else if (res.status === 401 && !stopped) {
        // Only the deactivation 401. Any other 401 (an expired access token)
        // is left to the api interceptor, whose refresh may still rescue it.
        const body = await res.json().catch(() => ({}));
        if (isAccountClosedDetail(body?.detail)) forceLogout(body.detail);
      }
    } catch {
      /* offline or the API is down — never sign out over a network blip */
    }
  };

  void beat();
  const timer = setInterval(() => void beat(), HEARTBEAT_MS);
  return () => {
    stopped = true;
    clearInterval(timer);
  };
}
